import { useCallback } from 'react';

import camelcaseKeys from 'camelcase-keys';
import { v4 as uuid } from 'uuid';

import { useSubscription } from './use_subscription';

interface OneOffHandlers<T> {
  connected?: (requestId: string) => void | Promise<void>;
  connectedError?: (error: unknown) => void;
  disconnected?: () => void;
  rejected?: () => void;
  received: (payload: T) => void;
}

interface SubscribeOptions<T> {
  camelize?: boolean;
  autoUnsubscribe?: boolean;
  isTerminal?: (payload: T) => boolean;
}

// useSubscription is a simple wrapper - this is a bit more opinionated, and tied to OneOffChannel
// to prevent a backend boilerplate proliferation
export function useOneOffSubscription() {
  const { subscribe, unsubscribe } = useSubscription();

  const subscribeOneOff = useCallback(
    <T>(
      handlers: OneOffHandlers<T>,
      { camelize = true, autoUnsubscribe = true, isTerminal }: SubscribeOptions<T> = {}
    ) => {
      const requestId = uuid();
      subscribe(
        { channel: 'OneOffChannel', request_id: requestId },
        {
          ...handlers,
          async connected() {
            try {
              await handlers.connected?.(requestId);
            } catch (error) {
              unsubscribe();
              handlers.connectedError?.(error);
            }
          },
          received(payload: object) {
            const data = (camelize ? camelcaseKeys(payload, { deep: true }) : payload) as T;
            handlers.received(data);

            const shouldUnsubscribe = isTerminal ? isTerminal(data) : autoUnsubscribe;
            if (shouldUnsubscribe) {
              unsubscribe();
            }
          },
        }
      );
    },
    [subscribe, unsubscribe]
  );

  return { subscribe: subscribeOneOff, unsubscribe };
}
